import React from "react";
import { withTranslation } from "react-i18next";
import Switch from "components/Switch";
import { FormGroup, FormLabel } from "react-bootstrap";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import SubmitButton from "components/SubmitButton";
import { PLAN_GRID_VIEWS } from "components/Plans";

const PDF_SCALE = 2;
/** Vista horaria: nitidez para impresión A4 sin el peso de scale 2 */
const PDF_HOURLY_SCALE = 1.65;
const PDF_GAP_PX = 24 * PDF_SCALE;
const PDF_PAGE_MARGIN_PT = 10;
const PX_TO_PT = 72 / 96;
/** Resolución objetivo al rasterizar en el PDF (A4 apaisado, impresión) */
const PDF_TARGET_DPI = 168;
const PDF_JPEG_QUALITY = 0.9;
/** Tamaño legible de los intervalos de turno en el documento impreso. */
const PDF_SHIFT_HOURS_FONT_SIZE_PX = 16;

const PDF_EXPORT_FILTER_STYLE_ID = "pdf-export-day-filter-style";
/** Vista funciones: más de 7 días → varias hojas con 7 días cada una */
const PDF_FUNCTIONS_DAYS_PER_PAGE = 7;
/**
 * html2canvas no rasteriza el borde inferior EXTERIOR de una tabla con
 * border-collapse (p. ej. la fila de totales en el tfoot de la tabla de
 * contadores). Para forzar que ese borde se dibuje, en el clon de exportación
 * añadimos una fila espaciadora vacía debajo de la fila de totales: así ese
 * borde pasa a ser INTERIOR (sí se rasteriza) y queda unos px por encima del
 * borde del canvas para que no se pierda al reescalar a A4.
 */
const PDF_TOTALS_SPACER_PX = 4;

async function captureElement(el, scale, rootId, { contentWidth } = {}) {
	const id = rootId || el.id;
	const ww = contentWidth || Math.max(el.scrollWidth, el.offsetWidth, el.clientWidth);
	const hasTotalsRow = !!el.querySelector?.(".counter-totals-row");
	const spacerPx = hasTotalsRow ? PDF_TOTALS_SPACER_PX : 0;
	// +1px extra para no recortar el borde inferior de la última fila al
	// rasterizar; +spacerPx reserva sitio para la fila espaciadora del tfoot.
	const wh =
		Math.max(el.scrollHeight, el.offsetHeight, el.clientHeight) + spacerPx + 1;
	return html2canvas(el, {
		backgroundColor: "#fff",
		scale,
		useCORS: true,
		width: ww,
		height: wh,
		windowWidth: ww,
		windowHeight: wh,
		scrollX: 0,
		scrollY: 0,
		onclone: (doc) => {
			const root = id ? doc.getElementById(id) : el;
			if (root) {
				root.style.overflow = "visible";
				root.style.maxWidth = "none";
				root.style.maxHeight = "none";

				// Pin the table to its natural content width so CSS width:100%
				// doesn't stretch cells and create empty whitespace in the PDF.
				if (contentWidth) {
					const table =
						root.querySelector("table") ||
						(root.tagName === "TABLE" ? root : null);
					if (table) {
						table.style.width = contentWidth + "px";
						table.style.minWidth = "0";
					}
				}

				let parent = root.parentElement;
				while (parent && parent !== doc.body) {
					parent.style.overflow = "visible";
					parent.style.maxWidth = "none";
					parent.style.maxHeight = "none";
					parent = parent.parentElement;
				}
			}

			const style = doc.createElement("style");
			const selector = id ? `#${id}` : ".pdf-export-slice";
			style.textContent = `
				${selector} input[type="checkbox"] {
					visibility: hidden !important;
					pointer-events: none !important;
				}
				${selector} .time-intervals .time-interval {
					font-size: ${PDF_SHIFT_HOURS_FONT_SIZE_PX}px !important;
				}
				.plan-icon.icon-locked {
					top: -3px !important;
					height: 20px !important;
				}
				${selector}.pdf-export-filter thead th,
				${selector}.pdf-export-filter tbody th {
					position: static !important;
					left: auto !important;
					top: auto !important;
				}
			`;
			doc.head.appendChild(style);

			if (root) {
				root.querySelectorAll("td, th").forEach((cell) => {
					const bg = window.getComputedStyle(cell).backgroundColor;
					if (bg && bg !== "transparent" && bg !== "rgba(0, 0, 0, 0)") {
						cell.style.backgroundColor = bg;
					}
					cell.style.boxShadow = "none";
				});
			}

			// Fila espaciadora bajo la fila de totales para que su borde inferior
			// se rasterice (ver PDF_TOTALS_SPACER_PX).
			if (root && spacerPx) {
				const totalsRow = root.querySelector(".counter-totals-row");
				if (totalsRow && totalsRow.parentNode) {
					const spacer = doc.createElement("tr");
					for (let k = 0; k < totalsRow.children.length; k++) {
						const td = doc.createElement("td");
						td.style.padding = "0";
						td.style.height = `${spacerPx}px`;
						td.style.border = "0";
						spacer.appendChild(td);
					}
					totalsRow.parentNode.appendChild(spacer);
				}
			}
		},
	});
}

function cropCanvasWidth(srcCanvas, targetWidthPx) {
	const tw = Math.round(targetWidthPx);
	if (!tw || tw >= srcCanvas.width) return srcCanvas;
	const out = document.createElement("canvas");
	out.width = tw;
	out.height = srcCanvas.height;
	const ctx = out.getContext("2d");
	ctx.drawImage(srcCanvas, 0, 0, tw, srcCanvas.height, 0, 0, tw, srcCanvas.height);
	return out;
}

function getInnerContentWidthPx(containerEl) {
	const table =
		containerEl.querySelector("table") ||
		(containerEl.tagName === "TABLE" ? containerEl : null);

	if (table) {
		// Collapse the table temporarily to measure its natural content width,
		// avoiding CSS width:100% stretching that creates whitespace in the PDF.
		const savedWidth = table.style.width;
		table.style.width = "1px";
		const naturalW = table.scrollWidth;
		table.style.width = savedWidth;
		if (naturalW > 0) return naturalW;
	}

	const inner =
		containerEl.querySelector("table") ||
		containerEl.firstElementChild ||
		containerEl;
	return (
		inner.scrollWidth ||
		inner.offsetWidth ||
		inner.clientWidth ||
		containerEl.scrollWidth
	);
}

function stitchHorizontal(canvases, gapPx) {
	if (!canvases.length) return null;
	const totalWidth = canvases.reduce(
		(w, cv, i) => w + (i ? gapPx : 0) + cv.width,
		0
	);
	const maxHeight = Math.max(...canvases.map((cv) => cv.height));
	const combined = document.createElement("canvas");
	combined.width = totalWidth;
	combined.height = maxHeight;
	const ctx = combined.getContext("2d");
	ctx.fillStyle = "#fff";
	ctx.fillRect(0, 0, combined.width, combined.height);
	let x = 0;
	canvases.forEach((cv, i) => {
		if (i) x += gapPx;
		ctx.drawImage(cv, x, 0);
		x += cv.width;
	});
	return combined;
}

function stitchVertical(canvases, gapPx) {
	if (!canvases.length) return null;
	const maxWidth = Math.max(...canvases.map((cv) => cv.width));
	const totalHeight = canvases.reduce(
		(h, cv, i) => h + (i ? gapPx : 0) + cv.height,
		0
	);
	const combined = document.createElement("canvas");
	combined.width = maxWidth;
	combined.height = totalHeight;
	const ctx = combined.getContext("2d");
	ctx.fillStyle = "#fff";
	ctx.fillRect(0, 0, combined.width, combined.height);
	let y = 0;
	canvases.forEach((cv, i) => {
		if (i) y += gapPx;
		ctx.drawImage(cv, 0, y);
		y += cv.height;
	});
	return combined;
}

async function captureCropped(el, scale, rootId) {
	// Measure natural content width before capture so the table renders
	// at that width in the clone (no CSS stretching → no empty whitespace).
	const contentWidth = getInnerContentWidthPx(el);
	return captureElement(el, scale, rootId, { contentWidth });
}

const waitForLayout = () =>
	new Promise((resolve) =>
		requestAnimationFrame(() => requestAnimationFrame(resolve))
	);

function getHourlyMetrics(planTable) {
	const dayCount = planTable.querySelectorAll("thead .head-hours-main").length;
	if (!dayCount) return null;
	const hourHeaders = planTable.querySelectorAll("thead .head-hours").length;
	const hoursPerDay = hourHeaders / dayCount;
	if (!hoursPerDay || hoursPerDay % 1 !== 0) return null;
	return { dayCount, hoursPerDay };
}

function getDefaultViewDayCount(planTable) {
	return planTable.querySelectorAll("thead .th-selectable").length;
}

function shouldChunkFunctionsExport(dayCount) {
	return dayCount > PDF_FUNCTIONS_DAYS_PER_PAGE;
}

function buildDayRangeFilterCss(tableId, startDayIndex, daysToShow, colsPerDay) {
	const showRules = [];

	for (let d = 0; d < daysToShow; d++) {
		const dayStart = 2 + (startDayIndex + d) * colsPerDay;
		const dayEnd = dayStart + colsPerDay - 1;

		for (let n = dayStart; n <= dayEnd; n++) {
			showRules.push(`
				#${tableId}.pdf-export-filter thead tr > th:nth-child(${n}),
				#${tableId}.pdf-export-filter tbody tr > th:nth-child(${n}),
				#${tableId}.pdf-export-filter tbody tr > td:nth-child(${n}) {
					display: table-cell !important;
				}
			`);
		}
	}

	return `
		#${tableId}.pdf-export-filter thead tr > *:not(:first-child),
		#${tableId}.pdf-export-filter tbody tr > *:not(:first-child) {
			display: none !important;
		}
		#${tableId}.pdf-export-filter thead tr > th:first-child,
		#${tableId}.pdf-export-filter tbody tr > th:first-child,
		#${tableId}.pdf-export-filter tbody tr > td:first-child {
			display: table-cell !important;
		}
		${showRules.join("\n")}
	`;
}

function buildDayFilterCss(tableId, dayIndex, hoursPerDay) {
	return buildDayRangeFilterCss(tableId, dayIndex, 1, 1 + hoursPerDay);
}

/** Oculta el resto de días en las tablas reales (mantiene estilos de plan-container). */
function applyPdfDayFilters(filters) {
	clearAllPdfDayFilters(filters.map((f) => f.tableId));

	if (!filters.length) return;

	let styleEl = document.getElementById(PDF_EXPORT_FILTER_STYLE_ID);
	if (!styleEl) {
		styleEl = document.createElement("style");
		styleEl.id = PDF_EXPORT_FILTER_STYLE_ID;
		document.head.appendChild(styleEl);
	}

	styleEl.textContent = filters
		.map((f) => {
			if (f.hoursPerDay != null) {
				return buildDayFilterCss(f.tableId, f.dayIndex, f.hoursPerDay);
			}
			return buildDayRangeFilterCss(
				f.tableId,
				f.startDayIndex,
				f.daysToShow,
				f.colsPerDay
			);
		})
		.join("\n");

	filters.forEach(({ tableId }) => {
		document.getElementById(tableId)?.classList.add("pdf-export-filter");
	});
}

function clearAllPdfDayFilters(tableIds) {
	tableIds.forEach((id) => {
		document.getElementById(id)?.classList.remove("pdf-export-filter");
	});
	const styleEl = document.getElementById(PDF_EXPORT_FILTER_STYLE_ID);
	if (styleEl) styleEl.remove();
}

function getNeedsTableEl() {
	const el =
		document.getElementById("tableNeedsImproved") ||
		document.getElementById("tableNeeds") ||
		document.getElementById("tablePlannedOnly");
	return el;
}

function getHeadHeight(tableEl) {
	const tr = tableEl.querySelector("thead tr");
	return tr ? tr.getBoundingClientRect().height : 32;
}

function getBodyRowHeights(tableEl) {
	const rows = tableEl.querySelectorAll("tbody tr");
	if (!rows.length) return [32, 32];
	return Array.from(rows).map((tr) => tr.getBoundingClientRect().height || 28);
}

function buildFirmaTableWithHeights({ rowHeights, theadHeight, heading }) {
	const wrapper = document.createElement("div");
	wrapper.className = "pdf-export-slice plan-container";
	wrapper.style.display = "inline-block";
	wrapper.style.background = "#fff";
	wrapper.style.position = "fixed";
	wrapper.style.left = "0";
	wrapper.style.top = "0";
	wrapper.style.zIndex = "99999";

	const table = document.createElement("table");
	table.style.borderCollapse = "collapse";
	table.style.width = "240px";

	const thead = document.createElement("thead");
	const trh = document.createElement("tr");
	const th = document.createElement("th");
	th.textContent = heading;
	Object.assign(th.style, {
		border: "1px solid #dee2e6",
		padding: "6px 8px",
		background: "#fff",
		textAlign: "left",
		height: theadHeight + "px",
		boxSizing: "border-box",
		whiteSpace: "nowrap",
	});
	trh.appendChild(th);
	thead.appendChild(trh);
	table.appendChild(thead);

	const tbody = document.createElement("tbody");
	rowHeights.forEach((h) => {
		const tr = document.createElement("tr");
		const td = document.createElement("td");
		td.innerHTML = "&nbsp;";
		Object.assign(td.style, {
			border: "1px solid #dee2e6",
			padding: "6px 8px",
			height: Math.max(28, h) + "px",
			boxSizing: "border-box",
		});
		tr.appendChild(td);
		tbody.appendChild(tr);
	});
	table.appendChild(tbody);

	wrapper.appendChild(table);
	return { wrapper, table };
}

function getA4PageSizePt(orientation) {
	return orientation === "p"
		? { width: 595.28, height: 841.89 }
		: { width: 841.89, height: 595.28 };
}

function getBestPageOrientation(canvas) {
	const availableScale = (orientation) => {
		const page = getA4PageSizePt(orientation);
		const maxW = page.width - PDF_PAGE_MARGIN_PT * 2;
		const maxH = page.height - PDF_PAGE_MARGIN_PT * 2;
		return Math.min(maxW / canvas.width, maxH / canvas.height);
	};

	return availableScale("p") > availableScale("l") ? "p" : "l";
}

function getA4MaxPx(orientation) {
	const page = getA4PageSizePt(orientation);
	const maxWPt = page.width - PDF_PAGE_MARGIN_PT * 2;
	const maxHPt = page.height - PDF_PAGE_MARGIN_PT * 2;
	return {
		width: Math.round((maxWPt * PDF_TARGET_DPI) / 72),
		height: Math.round((maxHPt * PDF_TARGET_DPI) / 72),
	};
}

function downscaleCanvasToFit(canvas, maxW, maxH) {
	const ratio = Math.min(maxW / canvas.width, maxH / canvas.height, 1);
	if (ratio >= 1) return canvas;

	const out = document.createElement("canvas");
	out.width = Math.max(1, Math.round(canvas.width * ratio));
	out.height = Math.max(1, Math.round(canvas.height * ratio));
	const ctx = out.getContext("2d");
	ctx.imageSmoothingEnabled = true;
	ctx.imageSmoothingQuality = "high";
	ctx.drawImage(canvas, 0, 0, out.width, out.height);
	return out;
}

function canvasToImageData(
	canvas,
	{ orientation, useJpeg = true, quality = PDF_JPEG_QUALITY } = {}
) {
	const { width: maxW, height: maxH } = getA4MaxPx(orientation);
	const prepared = downscaleCanvasToFit(canvas, maxW, maxH);

	if (useJpeg) {
		return {
			data: prepared.toDataURL("image/jpeg", quality),
			format: "JPEG",
			width: prepared.width,
			height: prepared.height,
		};
	}

	return {
		data: prepared.toDataURL("image/png"),
		format: "PNG",
		width: prepared.width,
		height: prepared.height,
	};
}

function addCanvasToPdf(
	pdf,
	canvas,
	pageIndex,
	{ orientation, useJpeg = true } = {}
) {
	if (pageIndex > 0) {
		pdf.addPage("a4", orientation);
	}

	const pageW = pdf.internal.pageSize.getWidth();
	const pageH = pdf.internal.pageSize.getHeight();
	const margin = PDF_PAGE_MARGIN_PT;
	const maxW = pageW - margin * 2;
	const maxH = pageH - margin * 2;

	const { data, format, width, height } = canvasToImageData(canvas, {
		orientation,
		useJpeg,
	});

	let imgW = width * PX_TO_PT;
	let imgH = height * PX_TO_PT;
	const fitScale = Math.min(maxW / imgW, maxH / imgH);
	imgW *= fitScale;
	imgH *= fitScale;

	const x = margin + (maxW - imgW) / 2;
	const y = margin + (maxH - imgH) / 2;

	pdf.addImage(data, format, x, y, imgW, imgH, undefined, "FAST");
}

function savePdfFromCanvas(combined, filename) {
	const orientation = getBestPageOrientation(combined);
	const pdf = new jsPDF({ orientation, unit: "pt", format: "a4" });
	addCanvasToPdf(pdf, combined, 0, { orientation, useJpeg: true });
	pdf.save(filename);
}

function savePdfFromPageCanvases(canvases, filename) {
	if (!canvases.length) return;
	const orientations = canvases.map(getBestPageOrientation);
	const pdf = new jsPDF({
		orientation: orientations[0],
		unit: "pt",
		format: "a4",
	});
	canvases.forEach((canvas, index) => {
		addCanvasToPdf(pdf, canvas, index, {
			orientation: orientations[index],
			useJpeg: true,
		});
	});
	pdf.save(filename);
}

class PlanExport2PDF extends React.Component {
	constructor(props) {
		super(props);

		this.state = {
			isLoading: false,
			submitted: false,
			addSignature: false,
			addCounters: false,
			addNeeds: false,
		};
	}

	isHourlyView = () => this.props.planGridView === PLAN_GRID_VIEWS.hours;

	isDefaultView = () => this.props.planGridView === PLAN_GRID_VIEWS.default;

	handleSwitch = (event) => {
		this.setState({
			[event.target.id]: event.target.checked,
		});
	};

	handleExport2PDF = async () => {
		if (this.isHourlyView()) {
			await this.handleExportHourlyPDF();
			return;
		}

		if (this.isDefaultView()) {
			const planTable = document.getElementById("tablePlan");
			const dayCount = planTable ? getDefaultViewDayCount(planTable) : 0;
			if (shouldChunkFunctionsExport(dayCount)) {
				await this.handleExportFunctionsChunkedPDF();
				return;
			}
		}

		await this.handleExportStandardPDF();
	};

	async handleExportStandardPDF() {
		const { addCounters, addSignature } = this.state;

		const left = document.getElementById("tablePlan");
		const right = document.getElementById("tableCounters");

		if (!left) {
			console.error("No se encontró #tablePlan");
			return;
		}

		const cPlan = await captureCropped(left, PDF_SCALE, "tablePlan");

		let cCounters = null;
		if (addCounters && right) {
			cCounters = await captureCropped(right, PDF_SCALE, "tableCounters");
		}

		let cFirma = null;
		if (addSignature) {
			const refEl = addCounters && right ? right : left;
			const rowHeights = getBodyRowHeights(refEl);
			const theadHeight = getHeadHeight(refEl);
			const firmaDom = buildFirmaTableWithHeights({
				rowHeights,
				theadHeight,
				heading: this.props.t('plan.export2pdf.signatureColumnHeading'),
			});
			document.body.appendChild(firmaDom.wrapper);
			await waitForLayout();
			cFirma = await captureElement(firmaDom.wrapper, PDF_SCALE);
			firmaDom.wrapper.remove();
		}

		const canvases = [cPlan]
			.concat(cCounters ? [cCounters] : [])
			.concat(cFirma ? [cFirma] : []);
		const combined = stitchHorizontal(canvases, PDF_GAP_PX);

		const parts = ["plan"];
		if (addCounters) parts.push("contadores");
		if (addSignature) parts.push("firma");
		savePdfFromCanvas(combined, parts.join("-") + ".pdf");
	}

	async handleExportFunctionsChunkedPDF() {
		const { addCounters, addSignature } = this.state;

		const planTable = document.getElementById("tablePlan");
		const countersTable = document.getElementById("tableCounters");

		if (!planTable) {
			console.error("No se encontró #tablePlan");
			return;
		}

		const dayCount = getDefaultViewDayCount(planTable);
		if (!dayCount) {
			console.error("No se pudo calcular el número de días del plan");
			return;
		}

		const captureScale = PDF_SCALE;
		const gapPx = 24 * captureScale;
		const filterTableIds = ["tablePlan"];

		let cCounters = null;
		if (addCounters && countersTable) {
			cCounters = await captureCropped(
				countersTable,
				captureScale,
				"tableCounters"
			);
		}

		const pageCanvases = [];

		try {
			for (
				let startDay = 0;
				startDay < dayCount;
				startDay += PDF_FUNCTIONS_DAYS_PER_PAGE
			) {
				const daysInChunk = Math.min(
					PDF_FUNCTIONS_DAYS_PER_PAGE,
					dayCount - startDay
				);

				applyPdfDayFilters([
					{
						tableId: "tablePlan",
						startDayIndex: startDay,
						daysToShow: daysInChunk,
						colsPerDay: 1,
					},
				]);
				await waitForLayout();

				const planCanvas = await captureCropped(
					planTable,
					captureScale,
					"tablePlan"
				);

				const rowCanvases = [planCanvas];

				if (addSignature) {
					const rowHeights = getBodyRowHeights(planTable);
					const theadHeight = getHeadHeight(planTable);
					const firmaDom = buildFirmaTableWithHeights({
						rowHeights,
						theadHeight,
						heading: "Firma trabajador",
					});
					document.body.appendChild(firmaDom.wrapper);
					await waitForLayout();
					const firmaCanvas = await captureElement(
						firmaDom.wrapper,
						captureScale
					);
					firmaDom.wrapper.remove();
					rowCanvases.push(firmaCanvas);
				}

				if (cCounters) {
					rowCanvases.push(cCounters);
				}

				pageCanvases.push(stitchHorizontal(rowCanvases, gapPx));
			}
		} finally {
			clearAllPdfDayFilters(filterTableIds);
		}

		const parts = ["plan"];
		if (addCounters) parts.push("contadores");
		if (addSignature) parts.push("firma");
		savePdfFromPageCanvases(pageCanvases, parts.join("-") + ".pdf");
	}

	async handleExportHourlyPDF() {
		const { addCounters, addSignature, addNeeds } = this.state;

		const planTable = document.getElementById("tablePlan");
		const countersTable = document.getElementById("tableCounters");
		const needsTable = addNeeds ? getNeedsTableEl() : null;

		if (!planTable) {
			console.error("No se encontró #tablePlan");
			return;
		}

		const metrics = getHourlyMetrics(planTable);
		if (!metrics) {
			console.error("No se pudo calcular la estructura horaria del plan");
			return;
		}

		const { dayCount, hoursPerDay } = metrics;
		const captureScale = PDF_HOURLY_SCALE;
		const gapPx = 24 * captureScale;
		const filterTableIds = ["tablePlan"];
		if (needsTable?.id) filterTableIds.push(needsTable.id);

		let cCounters = null;
		if (addCounters && countersTable) {
			cCounters = await captureCropped(
				countersTable,
				captureScale,
				"tableCounters"
			);
		}

		const dayBlocks = [];

		try {
			for (let dayIndex = 0; dayIndex < dayCount; dayIndex++) {
				const dayFilters = [
					{ tableId: "tablePlan", dayIndex, hoursPerDay },
				];
				if (needsTable) {
					dayFilters.push({
						tableId: needsTable.id,
						dayIndex,
						hoursPerDay,
					});
				}
				applyPdfDayFilters(dayFilters);
				await waitForLayout();

				const planCanvas = await captureCropped(
					planTable,
					captureScale,
					"tablePlan"
				);

				const rowCanvases = [planCanvas];

				if (addSignature) {
					const rowHeights = getBodyRowHeights(planTable);
					const theadHeight = getHeadHeight(planTable);
					const firmaDom = buildFirmaTableWithHeights({
						rowHeights,
						theadHeight,
						heading: "Firma trabajador",
					});
					document.body.appendChild(firmaDom.wrapper);
					await waitForLayout();
					const firmaCanvas = await captureElement(
						firmaDom.wrapper,
						captureScale
					);
					firmaDom.wrapper.remove();
					rowCanvases.push(firmaCanvas);
				}

				if (cCounters) {
					rowCanvases.push(cCounters);
				}

				const daySections = [stitchHorizontal(rowCanvases, gapPx)];

				if (addNeeds && needsTable) {
					const needsCanvas = await captureCropped(
						needsTable,
						captureScale,
						needsTable.id
					);
					daySections.push(needsCanvas);
				}

				dayBlocks.push(stitchVertical(daySections, gapPx));
			}
		} finally {
			clearAllPdfDayFilters(filterTableIds);
		}

		const parts = ["plan"];
		if (addNeeds && needsTable) parts.push("necesidades");
		if (addCounters) parts.push("contadores");
		if (addSignature) parts.push("firma");
		savePdfFromPageCanvases(dayBlocks, parts.join("-") + ".pdf");
	}

	handleSubmit = async (event) => {
		event.preventDefault();
		const props = this.props;
		this.setState({ isLoading: true });

		await this.handleExport2PDF();

		props.handleClose();
		this.setState({ isLoading: false });
	};

	render() {
		const { addCounters, addSignature, addNeeds } = this.state;
		const isHourly = this.isHourlyView();

		return (
			<form onSubmit={this.handleSubmit}>
				<div className="modal-body plan-opener-modal">
					<FormGroup className="form-group form-group-flex form-row-switch left form-group-main"
						controlId="addCounters"
					>
						<Switch
							controlId="addCounters"
							isOn={addCounters}
							handleToggle={this.handleSwitch}
						/>
						<FormLabel>
							{this.props.t('plan.export2pdf.addCounters')}
						</FormLabel>
					</FormGroup>

					<FormGroup className="form-group form-group-flex form-row-switch left form-group-main"
						controlId="addSignature"
					>
						<Switch
							controlId="addSignature"
							isOn={addSignature}
							handleToggle={this.handleSwitch}
						/>
						<FormLabel>
							{this.props.t('plan.export2pdf.addSignature')}
						</FormLabel>
					</FormGroup>

					{isHourly && (
						<FormGroup className="form-group form-group-flex form-row-switch left form-group-main"
							controlId="addNeeds"
						>
							<Switch
								controlId="addNeeds"
								isOn={addNeeds}
								handleToggle={this.handleSwitch}
							/>
							<FormLabel>Incluir tabla de necesidades</FormLabel>
						</FormGroup>
					)}
				</div>

				<div className="modal-footer mt25">
					<button
						type="button"
						onClick={this.props.handleClose}
						className="btn btn-outline-primary"
					>
						{this.props.t('common.cancel')}
					</button>
					<SubmitButton
						type="submit"
						isLoading={this.state.isLoading}
						text={this.props.t('plan.modal.exportPlan')}
						loadingText={this.props.t('plan.exporting')}
					/>
				</div>
			</form>
		);
	}
}

export default withTranslation()(PlanExport2PDF);
